import { GeolocationPosition, PermissionResult, PermissionType, Plugins } from '@capacitor/core';
import { OpenNativeSettings } from '@ionic-native/open-native-settings';

import { CustomEventNames, EventTypes, IMParticleCtx } from 'state/mParticle';
import { updateMParticleATTStatus } from 'state/mParticle/mparticle-att-plugin';
import { ILogRBIEvent } from 'state/mParticle/types';
import logger from 'utils/logger';

const { Permissions, Geolocation, PushNotifications, IOSAppTracking } = Plugins;

interface IAttTrackingOptions {
  requestPermission: boolean;
}

export interface IAttTrackingStatusResponse {
  value: string;
  status: 'authorized' | 'denied' | 'unrequested' | 'restricted';
}
interface IEnablePush {
  logRBIEvent?: IMParticleCtx['logRBIEvent'];
}

interface ISelectPermissions extends IEnablePush {}

export const enablePush = ({ logRBIEvent }: IEnablePush) =>
  // Get Push status from native
  Permissions.query({ name: PermissionType.Notifications })
    .then(res => {
      if (res.state === 'prompt') {
        // Capacitor API change
        // https://capacitorjs.com/docs/apis/push-notifications#requestpermission
        PushNotifications.requestPermission?.();
        PushNotifications.register();
      }
      if (['prompt', 'granted', 'denied'].indexOf(res.state) > -1) {
        logRBI(res, logRBIEvent);
      }
      return true;
    })
    .catch((error: Error) => {
      logger.error({ error, message: 'Prompt For Push Permissions error' });
    });

export const openPushSetting = () =>
  // Open Push settings in native
  OpenNativeSettings?.open('application_details');

const selectPermissionState = async () =>
  await Permissions.query({ name: PermissionType.Notifications });

export const isNotificationsPermissionGranted = async () => {
  const { state } = await selectPermissionState();
  return state === 'granted';
};

export const isNotificationsPermissionDenied = async () => {
  const { state } = await selectPermissionState();
  return state === 'denied';
};

export const selectPermission = async ({ logRBIEvent }: ISelectPermissions) => {
  const result = await selectPermissionState();
  logRBI(result, logRBIEvent);

  const { state } = result;
  if (state === 'prompt') {
    const { granted } = await PushNotifications.requestPermission?.();
    PushNotifications.register();

    return granted;
  }
  return state === 'granted';
};

export const enableLocation = (timeout: number): Promise<GeolocationPosition | void> =>
  Permissions.query({ name: PermissionType.Geolocation })
    .then(res => {
      switch (res.state) {
        case 'prompt':
        case 'granted':
          return Geolocation.getCurrentPosition({ timeout });
        case 'denied':
        default:
          return;
      }
    })
    .catch((error: Error) => {
      // If the user denies when prompted, Capacitor returns an error with the following message,
      if (error.message === 'User denied location permission') {
        return Promise.reject(error.message);
      }
      // Capacitor is timing out and returning error:
      // {"message":"The operation couldn’t be completed. (kCLErrorDomain error 1.)",
      // "errorMessage":"The operation couldn’t be completed. (kCLErrorDomain error 1.)"}
      const message = 'Prompt For Location Permissions error';
      logger.error({ error, message });
      return Promise.reject(message);
    });

export const appTrackingTransparency = async ({
  requestPermission,
}: IAttTrackingOptions): Promise<IAttTrackingStatusResponse | undefined> => {
  try {
    const response = await IOSAppTracking.getTrackingStatus();
    if (response.status === 'unrequested' && requestPermission) {
      const result = await IOSAppTracking.requestPermission();

      // Update mParticle att status after we have requested permissions
      updateMParticleATTStatus();

      return result;
    }
    return response;
  } catch (error) {
    logger.error({
      error,
      message: 'Error in fetching user App Tracking Transparency status',
      requestPermission,
    });
  }
  return undefined;
};
function logRBI(permissionResult: PermissionResult, logRBIEvent?: ILogRBIEvent) {
  if (logRBIEvent) {
    logRBIEvent({
      name: CustomEventNames.PUSH_NOTIFICATIONS_RESPONSE,
      type: EventTypes.Other,
      attributes: { response: permissionResult.state },
    });
  }
}
